<template>
  <v-app>
    <v-main
      v-if="preloading"
      class="secondary"
      style="padding-top: 56px;"
    >
      <h-preloader />
    </v-main>
    <template v-else>
      <h-block-screen v-if="screenBlocked" />
      <h-lock-screen v-if="screenLocked" />
      <h-loading />
      <div v-if="$page.props.brand === 'SBL'">
        <h-unpaid-block
          v-if="showUnpaidBlock"
          :payment-link="paymentLink"
          :error="unpaidBlockError"
        />
        <h-unpaid-warning
          v-model="showUnpaidWarning"
          :payment-link="paymentLink"
        />
      </div>
      <h-alert />
      <h-toolbar @drawer="drawer = $event" />
      <h-drawer v-model="drawer" />
      <v-main class="secondary">
        <v-container
          fluid
          class="pb-16"
          style="max-width: 600px"
        >
          <slot />
        </v-container>
      </v-main>
    </template>
  </v-app>
</template>

<script>
  import axios from 'axios'
  import { Inertia } from '@inertiajs/inertia'
  import DB from '@/Mixins/DB'
  import Hashes from '@/Mixins/Hashes'
  import LoadsLocale from '@/Mixins/LoadsLocale'
  import Loggable from '@/Mixins/Loggable'
  import Synchronizable from '@/Mixins/Synchronizable'
  import FranceLog from '@/Models/Static/FiscalCountry/FranceLog'
  import HAlert from '@/Components/Messages/Global'
  import HBlockScreen from '@/Components/BlockScreen'
  import HDrawer from '@/Components/Navigation/Drawer'
  import HLoading from '@/Components/Loading'
  import HLockScreen from '@/Components/LockScreen/Pin'
  import HPreloader from '@/Components/Preloader'
  import HToolbar from '@/Components/Navigation/Toolbar'
  import HUnpaidBlock from '@/Components/Messages/UnpaidBlock'
  import HUnpaidWarning from '@/Components/Messages/UnpaidWarning'
  import Version from '@/version.json'

  export default {
    mixins: [
      DB,
      Hashes,
      LoadsLocale,
      Loggable,
      Synchronizable,
    ],

    components: {
      HAlert,
      HBlockScreen,
      HDrawer,
      HLoading,
      HLockScreen,
      HPreloader,
      HToolbar,
      HUnpaidBlock,
      HUnpaidWarning,
    },

    data: () => ({
      FranceLog,
      drawer: false,
      preloading: true,
      showUnpaidBlock: false,
      showUnpaidWarning: false,
      unpaidBlockError: '',
      paymentLink: '',
      Version,
    }),

    computed: {
      firstTenantUser () {
        return this.$store.state.rummage.data.TenantUser?.find(o => !! o.active)
      },
      isDemo () {
        return this.$page.props.auth.demo
      },
    },

    async created () {
      this.$vuetify.theme.dark = true
      if (! this.isDemo) {
        await this.$store.dispatch('lockScreen')
      }
    },

    async mounted () {
      this.$store.commit('androidVersion', AndroidDevice.version())
      this.loadUUID()

      const tenant = this.hash(this.$page.props.auth.tenant)
      this.loadSettings(tenant)

      this.$store.commit('workingModes/setUpEventMode')

      await this.openDB()
      if (localStorage.getItem('tenant') != tenant || ! localStorage.getItem('syncFrom')) {
        await this.truncateDB()
      }
      localStorage.setItem('tenant', tenant)
      await this.loadToken()
      await this.syncDatabase()
      await this.loadAllIntoMemory()

      if (! this.appIsServiceable) {
        this.preloading = false
        this.$store.commit('user/block')
        return
      }

      this.$logFrance(this.FranceLog.START_OF_TERMINAL)
      this.startShouldSync()
      this.checkVersion()
      this.requestInvoiceStatus()
      if (this.isDemo) {
        this.$store.dispatch('unlockScreen', this.firstTenantUser)
      } else {
        AndroidDevice.setPinTimeout(this.$store.getters['orders/config']('pinTimeoutSeconds') ?? 60)
      }
      this.preloading = false

      this.queueSendLog(10000)
    },

    async beforeDestroy () {
      AndroidDevice.setPinTimeout(0)
      this.$store.dispatch('sync/stop')
      this.$store.commit('user/unblock')
      this.$store.commit('workingModes/tearDownEventMode')
      this.stopShouldSync()
      this.stopSendLog()
      await this.truncateDB()
    },

    methods: {
      loadUUID () {
        let UUID = localStorage.getItem('UUID')
        if (! UUID) {
          UUID = AndroidDevice.UUID()
          localStorage.setItem('UUID', UUID)
        }
        this.$store.commit('UUID', UUID)
      },
      loadSettings (tenant) {
        tenant === localStorage.getItem('tenant')
          ? this.$store.dispatch('settings/load')
          : localStorage.removeItem('settings')
      },
      async loadToken () {
        let token = this.$page.props.token
        if (token) {
          await this.setKeyval('token', token)
        } else {
          token = await this.getKeyval('token')
        }
        if (! token) {
          Inertia.delete('')
          throw new Error('No token found, redirected to login')
        }
        this.$store.commit('token', token)
      },
      checkVersion () {
        const oldVersion = localStorage.getItem('version')
        const newVersion = this.Version.short
        if (oldVersion !== newVersion) {
          localStorage.setItem('version', newVersion)
          this.$log(`Updated the version from ${oldVersion} to ${newVersion}`)
          this.$logFrance(this.FranceLog.VERSION_OF_THE_SOFTWARE, newVersion)
        }
      },
      requestInvoiceStatus () {
        if (this.$page.props.brand !== 'SBL' || this.isDemo) {
          return
        }
        axios.post('/invoice-status')
          .then(r => {
            if (r.data.block === undefined) {
              return // last invoice already paid
            }
            this.paymentLink = r.data.link
            if (r.data.block) {
              this.showUnpaidBlock = true
              this.unpaidBlockError = r.data.error || ''
            } else {
              this.showUnpaidWarning = true
            }
          })
          .catch(e => console.warn('Invoice Status request failed'))
      },
    },
  }
</script>

<style lang="scss">
  html body {
    cursor: default;
  }
</style>
